import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, CURRENCIES, currentMonth, monthLabel } from "@/lib/format";
import { Button, Input, Select, Card, Modal, PageHeader } from "@/components/Kit";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

export default function Budgets() {
  const { user } = useAuth();
  const cur = user?.baseCurrency || "USD";
  const [month, setMonth] = useState(currentMonth());
  const [budgets, setBudgets] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ categoryId: "", amount: "", currency: cur });
  const [saving, setSaving] = useState(false);

  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const expenseCats = cats.filter((c) => c.type === "EXPENSE");

  const load = async () => {
    const [b, c] = await Promise.all([api.get(`/budgets?month=${month}`), api.get("/categories")]);
    setBudgets(b.data);
    setCats(c.data);
  };
  useEffect(() => { load(); }, [month]);

  const openNew = () => {
    setEditing(null);
    setForm({ categoryId: expenseCats[0]?.id || "", amount: "", currency: cur });
    setOpen(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ categoryId: b.categoryId, amount: String(b.amount), currency: b.currency });
    setOpen(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { categoryId: form.categoryId, amount: parseFloat(form.amount), currency: form.currency, month };
      if (editing) await api.put(`/budgets/${editing.id}`, payload);
      else await api.post("/budgets", payload);
      toast.success(editing ? "Budget updated" : "Budget created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/budgets/${id}`); toast.success("Budget deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <PageHeader title="Budgets" subtitle={`Spending limits for ${monthLabel(month)}`}>
        <div className="flex items-center gap-3">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="budget-month"
            className="bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white outline-none focus:border-cyan/50 num" />
          <Button onClick={openNew} data-testid="add-budget-btn"><Plus size={16} weight="bold" /> Add Budget</Button>
        </div>
      </PageHeader>

      {budgets.length === 0 ? (
        <Card className="p-16 text-center text-sm text-[#52525B]">No budgets set for this month.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {budgets.map((b) => {
            const cat = catMap[b.categoryId];
            const limit = Number(b.amountInBase || 0);
            const spent = Number(b.spent || 0);
            const pct = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
            const over = spent > limit;
            const barColor = over ? "#F43F5E" : pct > 80 ? "#F59E0B" : "#00E5FF";
            return (
              <Card key={b.id} className="p-6 group" data-testid="budget-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: cat?.color || "#A1A1AA" }} />
                    <span className="font-semibold">{cat?.name || "Category"}</span>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(b)} data-testid="edit-budget-btn" className="text-[#52525B] hover:text-cyan"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(b.id)} data-testid="delete-budget-btn" className="text-[#52525B] hover:text-expense"><Trash size={16} /></button>
                  </div>
                </div>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="num text-2xl font-medium" style={{ color: barColor }}>{fmt(spent, cur)}</span>
                  <span className="num text-sm text-[#52525B]">/ {fmt(limit, cur)}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                </div>
                <p className="text-xs mt-2" style={{ color: over ? "#F43F5E" : "#52525B" }}>
                  {over ? `Over budget by ${fmt(spent - limit, cur)}` : `${fmt(limit - spent, cur)} remaining`}
                </p>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Budget" : "Add Budget"}>
        <form onSubmit={save} className="space-y-4">
          <Select label="Category" value={form.categoryId} onChange={set("categoryId")} required data-testid="budget-category">
            <option value="">Select category</option>
            {expenseCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input label="Limit" type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} placeholder="0.00" required data-testid="budget-amount" />
            </div>
            <Select label="Currency" value={form.currency} onChange={set("currency")} data-testid="budget-currency">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <Button type="submit" disabled={saving} className="w-full" data-testid="save-budget-btn">
            {saving ? "Saving..." : editing ? "Update" : "Create Budget"}
          </Button>
        </form>
      </Modal>
    </div>
  );
}
